/**
 * ClientUsersKeysSection — admin management of an org's users and API keys,
 * embedded on the admin client editor. List + activate/deactivate + remove users,
 * and list + revoke API keys (any org). Reachable by SystemAdmins and by users
 * granted CanManageClients; a handful of actions (role change, Invoicing/Client
 * management grants, API keys) stay SystemAdmin-only on the backend and are
 * hidden here for a CanManageClients-only user.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Key, Loader2, UserX, UserCheck, Trash2, Ban, RefreshCw, Plus, Copy, Check, ChevronDown, Receipt, Shield, Mail, MoreVertical, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getErrorMessage } from '../../services/apiService';
import ConfirmDialog, { useConfirm } from '../ConfirmDialog';
import {
  listClientUsersAdmin, toggleClientUserActive, removeClientUser, changeClientUserRole,
  setClientUserInvoicingAccess, setClientUserClientAdminAccess,
  setClientUserPermissions,
  listClientApiKeysAdmin, revokeClientApiKey, createClientApiKey,
  inviteUserAdmin, listClientInvitationsAdmin, resendInvitationAdmin,
  AdminClientUser, AdminApiKey, PendingInvitation,
} from '../../services/clientsAdminService';

const ROLE_OPTIONS = ['ClientUser', 'ClientAdmin'];

const Card: React.FC<{ title: string; icon: React.ReactNode; onRefresh: () => void; children: React.ReactNode }> = ({ title, icon, onRefresh, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden">
      <div
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-between px-6 py-4 bg-black/[0.02] hover:bg-black/[0.04] transition-colors cursor-pointer ${open ? 'border-b border-black/[0.08]' : ''}`}
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform ${open ? '' : '-rotate-90'}`} />
          <h3 className="text-sm font-bold text-[#0f172a] flex items-center gap-2">{icon} {title}</h3>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onRefresh(); }} className="text-xs text-[#64748b] hover:text-[#c41e3a] inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>
      {open && <div className="px-6 py-5">{children}</div>}
    </div>
  );
};

type PermKey = keyof Pick<AdminClientUser, 'canAccessNmvtis' | 'canAccessTheftLien' | 'canAccessMotorVehicleRecord' | 'canAccessVinDecoder' | 'canAccessAstsPublic' | 'mfaEnabled'>;
const PERMISSION_LABELS: { key: PermKey; label: string }[] = [
  { key: 'canAccessMotorVehicleRecord', label: 'MVR' },
  { key: 'canAccessNmvtis', label: 'NMVTIS' },
  { key: 'canAccessTheftLien', label: 'Theft/Lien' },
  { key: 'canAccessVinDecoder', label: 'VIN Decoder' },
  { key: 'canAccessAstsPublic', label: 'ASTS Public' },
  { key: 'mfaEnabled', label: 'MFA' },
];

const ClientUsersKeysSection: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { user: currentUser } = useAuth();
  const isSystemAdmin = currentUser?.isSystemAdmin === true;
  const [users, setUsers] = useState<AdminClientUser[]>([]);
  const [keys, setKeys] = useState<AdminApiKey[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();
  const [expandedPerms, setExpandedPerms] = useState<string | null>(null);
  const [resentInvitation, setResentInvitation] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', email: '', role: 'ClientUser' as 'ClientUser' | 'ClientAdmin' });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      // API keys are SystemAdmin-only on the backend — skip the call entirely for a
      // CanManageClients-only user so it doesn't just come back a 403.
      const [u, k, inv] = await Promise.all([
        listClientUsersAdmin(clientId),
        isSystemAdmin ? listClientApiKeysAdmin(clientId) : Promise.resolve([]),
        listClientInvitationsAdmin(clientId),
      ]);
      setUsers(u);
      setKeys(k);
      setInvitations(inv);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, isSystemAdmin]);

  useEffect(() => { load(); }, [load]);

  const doToggle = async (u: AdminClientUser) => {
    setBusy(u.id); setError('');
    try { await toggleClientUserActive(u.id); setUsers((p) => p.map((x) => x.id === u.id ? { ...x, isActive: !x.isActive } : x)); }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const askRemove = (u: AdminClientUser) => {
    confirm({
      title: 'Remove this user?',
      message: (
        <>
          <span className="font-semibold text-[#0f172a]">{u.firstName} {u.lastName}</span> ({u.email})
          is deactivated immediately and marked for deletion. They lose access as soon as this is done.
        </>
      ),
      confirmLabel: 'Remove user',
      tone: 'danger',
      action: () => doRemove(u),
    });
  };

  const doRemove = async (u: AdminClientUser) => {
    setBusy(u.id); setError('');
    try { await removeClientUser(u.id); setUsers((p) => p.map((x) => x.id === u.id ? { ...x, isActive: false, markedForDeletionAt: new Date().toISOString() } : x)); }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const askRevoke = (k: AdminApiKey) => {
    confirm({
      title: 'Revoke this API key?',
      message: (
        <>
          Any integration still using <span className="font-semibold text-[#0f172a]">{k.name}</span>{' '}
          stops working immediately. This cannot be undone — a replacement key has to be issued.
        </>
      ),
      confirmLabel: 'Revoke key',
      tone: 'danger',
      action: () => doRevoke(k),
    });
  };

  const doRevoke = async (k: AdminApiKey) => {
    setBusy(k.id); setError('');
    try { await revokeClientApiKey(k.id); setKeys((p) => p.map((x) => x.id === k.id ? { ...x, isActive: false, revokedAt: new Date().toISOString() } : x)); }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const doChangeRole = async (u: AdminClientUser, role: string) => {
    if (role === u.role) return;
    setBusy(u.id); setError('');
    try { await changeClientUserRole(u.id, role); setUsers((p) => p.map((x) => x.id === u.id ? { ...x, role } : x)); }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const doToggleInvoicing = async (u: AdminClientUser) => {
    const next = !u.canAccessInvoicing;
    setBusy(u.id); setError('');
    try { await setClientUserInvoicingAccess(u.id, next); setUsers((p) => p.map((x) => x.id === u.id ? { ...x, canAccessInvoicing: next } : x)); }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const doToggleClientAdminAccess = async (u: AdminClientUser) => {
    const next = !u.canManageClients;
    setBusy(u.id); setError('');
    try { await setClientUserClientAdminAccess(u.id, next); setUsers((p) => p.map((x) => x.id === u.id ? { ...x, canManageClients: next } : x)); }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const doTogglePermission = async (u: AdminClientUser, key: PermKey) => {
    const updated = { ...u, [key]: !u[key] };
    setBusy(`${u.id}-${key}`); setError('');
    try {
      await setClientUserPermissions(u.id, {
        canAccessNmvtis: updated.canAccessNmvtis,
        canAccessTheftLien: updated.canAccessTheftLien,
        canAccessMotorVehicleRecord: updated.canAccessMotorVehicleRecord,
        canAccessVinDecoder: updated.canAccessVinDecoder,
        canAccessAstsPublic: updated.canAccessAstsPublic,
        mfaEnabled: updated.mfaEnabled,
      });
      setUsers((p) => p.map((x) => x.id === u.id ? { ...x, [key]: updated[key] } : x));
    }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const doTogglePortalAlerts = async (u: AdminClientUser) => {
    const next = !u.receivePortalAlerts;
    const busyKey = `${u.id}-portalAlerts`;
    setBusy(busyKey); setError('');
    try {
      await setClientUserPermissions(u.id, {
        canAccessNmvtis: u.canAccessNmvtis,
        canAccessTheftLien: u.canAccessTheftLien,
        canAccessMotorVehicleRecord: u.canAccessMotorVehicleRecord,
        canAccessVinDecoder: u.canAccessVinDecoder,
        canAccessAstsPublic: u.canAccessAstsPublic,
        mfaEnabled: u.mfaEnabled,
        receivePortalAlerts: next,
      });
      setUsers((p) => p.map((x) => x.id === u.id ? { ...x, receivePortalAlerts: next } : x));
    }
    catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const doResend = async (inv: PendingInvitation) => {
    setBusy(`resend-${inv.id}`); setError('');
    try {
      await resendInvitationAdmin(inv.id);
      setResentInvitation(inv.id);
      setTimeout(() => setResentInvitation(null), 4000);
      await load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const doInvite = async () => {
    if (!inviteForm.email.trim() || !inviteForm.firstName.trim() || !inviteForm.lastName.trim()) {
      setInviteError('First name, last name and email are required.'); return;
    }
    setInviting(true); setInviteError(''); setInviteSuccess('');
    try {
      await inviteUserAdmin(clientId, inviteForm);
      setInviteSuccess(`Invitation sent to ${inviteForm.email}.`);
      setInviteForm({ firstName: '', lastName: '', email: '', role: 'ClientUser' });
      setShowInvite(false);
      await load();
    } catch (err) { setInviteError(getErrorMessage(err)); }
    finally { setInviting(false); }
  };

  const doGenerateKey = async () => {
    const name = window.prompt('Name this organization API key (e.g. "Production integration"):');
    if (name == null) return;
    if (!name.trim()) { setError('A key name is required.'); return; }
    setBusy('new-key'); setError(''); setNewKeySecret(null); setCopied(false);
    try {
      const created = await createClientApiKey(clientId, name.trim());
      setNewKeySecret(created.apiKey);
      await load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setBusy(null); }
  };

  const copySecret = async () => {
    if (!newKeySecret) return;
    try { await navigator.clipboard.writeText(newKeySecret); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard unavailable — user can select manually */ }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-black/[0.08] p-8 text-center text-[#64748b]">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading users &amp; API keys…
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>
      )}

      <Card title={`Users (${users.length})`} icon={<Users className="w-4 h-4 text-[#c41e3a]" />} onRefresh={load}>
        <div className="flex justify-end mb-3">
          <button onClick={() => { setShowInvite((v) => !v); setInviteError(''); setInviteSuccess(''); }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830]">
            <Plus className="w-3.5 h-3.5" /> Invite user
          </button>
        </div>
        {inviteSuccess && <div className="mb-3 p-2.5 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">{inviteSuccess}</div>}
        {showInvite && (
          <div className="mb-4 p-4 rounded-xl border border-black/[0.08] bg-[#f8fafc] space-y-3">
            {inviteError && <div className="p-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{inviteError}</div>}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">First name *</label>
                <input value={inviteForm.firstName} onChange={(e) => setInviteForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="w-full px-3 py-1.5 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Last name *</label>
                <input value={inviteForm.lastName} onChange={(e) => setInviteForm((f) => ({ ...f, lastName: e.target.value }))}
                  className="w-full px-3 py-1.5 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Email *</label>
              <input type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-1.5 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#94a3b8] mb-1">Role</label>
              <select value={inviteForm.role} onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value as 'ClientUser' | 'ClientAdmin' }))}
                className="w-full px-3 py-1.5 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-[#c41e3a]/50 bg-white">
                <option value="ClientUser">ClientUser</option>
                <option value="ClientAdmin">ClientAdmin</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => { setShowInvite(false); setInviteError(''); }} disabled={inviting}
                className="px-3 py-1.5 rounded-lg border border-black/[0.08] text-[#475569] text-xs hover:bg-black/[0.03] disabled:opacity-50">Cancel</button>
              <button onClick={doInvite} disabled={inviting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#c41e3a] text-white text-xs font-semibold hover:bg-[#a51830] disabled:opacity-50">
                {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Send invitation
              </button>
            </div>
          </div>
        )}
        {users.length === 0 ? (
          <p className="text-sm text-[#94a3b8]">No users yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
                <tr><th className="text-left py-2">Name</th><th className="text-left">Email</th><th className="text-left">Role</th><th className="text-left">Status</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <React.Fragment key={u.id}>
                  <tr className="border-b border-black/[0.04]">
                    <td className="py-2.5 text-[#0f172a] font-medium">{u.firstName} {u.lastName}</td>
                    <td className="text-[#64748b]">{u.email}</td>
                    <td>
                      {isSystemAdmin ? (
                        <select
                          value={u.role}
                          onChange={(e) => doChangeRole(u, e.target.value)}
                          disabled={busy === u.id || !!u.markedForDeletionAt}
                          className="text-xs px-2 py-1 rounded border border-black/[0.1] bg-white text-[#475569] focus:outline-none focus:border-[#c41e3a]/50 disabled:opacity-40"
                        >
                          {!ROLE_OPTIONS.includes(u.role) && <option value={u.role}>{u.role}</option>}
                          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : (
                        <span className="text-xs text-[#475569]">{u.role}</span>
                      )}
                    </td>
                    <td>
                      {u.markedForDeletionAt
                        ? <span className="text-xs text-red-600">Removed</span>
                        : u.isActive
                          ? <span className="text-xs text-green-600">Active</span>
                          : <span className="text-xs text-amber-600">Inactive</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setExpandedPerms(expandedPerms === u.id ? null : u.id)} disabled={!!u.markedForDeletionAt}
                          title="Manage section permissions"
                          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border disabled:opacity-40 ${expandedPerms === u.id ? 'border-[#c41e3a]/40 bg-[#c41e3a]/10 text-[#c41e3a]' : 'border-black/[0.1] text-[#475569] hover:bg-black/[0.03]'}`}>
                          <Shield className="w-3.5 h-3.5" /> Permissions
                        </button>
                        {isSystemAdmin && (
                          <button onClick={() => doToggleInvoicing(u)} disabled={busy === u.id || !!u.markedForDeletionAt}
                            title="Toggle Invoicing access"
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border disabled:opacity-40 ${u.canAccessInvoicing ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' : 'border-black/[0.1] text-[#475569] hover:bg-black/[0.03]'}`}>
                            <Receipt className="w-3.5 h-3.5" />{u.canAccessInvoicing ? ' ✓' : ''}
                          </button>
                        )}
                        {isSystemAdmin && (
                          <button onClick={() => doToggleClientAdminAccess(u)} disabled={busy === u.id || !!u.markedForDeletionAt}
                            title="Toggle Manage Clients access"
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border disabled:opacity-40 ${u.canManageClients ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' : 'border-black/[0.1] text-[#475569] hover:bg-black/[0.03]'}`}>
                            <Building2 className="w-3.5 h-3.5" />{u.canManageClients ? ' ✓' : ''}
                          </button>
                        )}
                        <div className="relative" ref={openMenu === u.id ? menuRef : undefined}>
                          <button onClick={() => setOpenMenu(openMenu === u.id ? null : u.id)} disabled={!!u.markedForDeletionAt}
                            title="More actions"
                            className="inline-flex items-center justify-center w-7 h-7 rounded border border-black/[0.1] text-[#475569] hover:bg-black/[0.03] disabled:opacity-40">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                          {openMenu === u.id && (
                            <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-black/[0.08] py-1 z-50">
                              <button onClick={() => { doToggle(u); setOpenMenu(null); }} disabled={busy === u.id}
                                className="w-full text-left px-3 py-1.5 text-xs text-[#475569] hover:bg-black/[0.04] flex items-center gap-2 disabled:opacity-40">
                                {u.isActive ? <><Ban className="w-3.5 h-3.5" /> Deactivate</> : <><UserCheck className="w-3.5 h-3.5" /> Activate</>}
                              </button>
                              <button onClick={() => { askRemove(u); setOpenMenu(null); }} disabled={busy === u.id || !!u.markedForDeletionAt}
                                className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-40">
                                {busy === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserX className="w-3.5 h-3.5" />} Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  {expandedPerms === u.id && (
                    <tr className="bg-[#f8fafc] border-b border-black/[0.04]">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-[#64748b] mr-1">Section access:</span>
                          {PERMISSION_LABELS.map(({ key, label }) => {
                            const enabled = u[key] as boolean;
                            const busyKey = `${u.id}-${key}`;
                            return (
                              <button
                                key={key}
                                onClick={() => doTogglePermission(u, key)}
                                disabled={busy === busyKey}
                                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-medium transition-colors disabled:opacity-50 ${enabled ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' : 'border-black/[0.1] bg-white text-[#94a3b8] hover:bg-black/[0.03] hover:text-[#475569]'}`}
                              >
                                {busy === busyKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className={`w-1.5 h-1.5 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-300'}`} />}
                                {label}
                              </button>
                            );
                          })}
                          {u.isSystemAdmin && (
                            <button
                              onClick={() => doTogglePortalAlerts(u)}
                              disabled={busy === `${u.id}-portalAlerts`}
                              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-medium transition-colors disabled:opacity-50 ${u.receivePortalAlerts ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' : 'border-black/[0.1] bg-white text-[#94a3b8] hover:bg-black/[0.03] hover:text-[#475569]'}`}
                            >
                              {busy === `${u.id}-portalAlerts` ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className={`w-1.5 h-1.5 rounded-full ${u.receivePortalAlerts ? 'bg-green-500' : 'bg-gray-300'}`} />}
                              Portal Alerts
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(() => {
        const pending = invitations.filter(i => i.status === 'Pending');
        const accepted = invitations.filter(i => i.status === 'Accepted');
        const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
        return (
          <>
            {pending.length > 0 && (
              <Card title={`Pending Invitations (${pending.length})`} icon={<Mail className="w-4 h-4 text-amber-500" />} onRefresh={load}>
                <p className="text-xs text-[#64748b] mb-3">These accounts haven't been set up yet. Click "Resend" to send a new invitation email with a fresh link and extended expiry.</p>
                <div className="space-y-2">
                  {pending.map((inv) => (
                    <div key={inv.id} className="rounded-xl border border-amber-200 p-3 bg-amber-50">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-[#0f172a]">{inv.firstName} {inv.lastName}</p>
                          <p className="text-xs text-[#64748b]">{inv.email} · {inv.role}</p>
                          <p className="text-xs text-[#94a3b8]">Sent {fmt(inv.createdAt)} · Expires {fmt(inv.expiresAt)}</p>
                        </div>
                        <button
                          onClick={() => doResend(inv)}
                          disabled={busy === `resend-${inv.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-50 whitespace-nowrap"
                        >
                          {busy === `resend-${inv.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : resentInvitation === inv.id ? <Check className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                          {resentInvitation === inv.id ? 'Sent!' : 'Resend'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {accepted.length > 0 && (
              <Card title={`Accepted Invitations (${accepted.length})`} icon={<Check className="w-4 h-4 text-green-500" />} onRefresh={load}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
                      <tr>
                        <th className="text-left py-2">Name</th>
                        <th className="text-left">Email</th>
                        <th className="text-left">Role</th>
                        <th className="text-left">Accepted</th>
                        <th className="text-left">Last Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accepted.map((inv) => (
                        <tr key={inv.id} className="border-b border-black/[0.04] last:border-0">
                          <td className="py-2.5 font-medium text-[#0f172a]">{inv.firstName} {inv.lastName}</td>
                          <td className="text-[#64748b]">{inv.email}</td>
                          <td className="text-[#64748b]">{inv.role}</td>
                          <td className="text-[#64748b]">{fmt(inv.acceptedAt)}</td>
                          <td className={inv.lastLoginAt ? 'text-[#64748b]' : 'text-amber-600 font-medium'}>
                            {inv.lastLoginAt ? fmt(inv.lastLoginAt) : 'Never logged in'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        );
      })()}

      {isSystemAdmin && (
      <Card title={`API Keys (${keys.length})`} icon={<Key className="w-4 h-4 text-[#c41e3a]" />} onRefresh={load}>
        <div className="mb-4">
          <button onClick={doGenerateKey} disabled={busy === 'new-key'}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-50">
            {busy === 'new-key' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Generate API key
          </button>
        </div>

        {newKeySecret && (
          <div className="mb-4 p-4 rounded-lg border border-amber-300 bg-amber-50">
            <p className="text-xs font-semibold text-amber-900 mb-2">
              Copy this key now — it's shown only once and cannot be retrieved later.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded bg-white border border-amber-200 text-xs font-mono text-[#0f172a] break-all">{newKeySecret}</code>
              <button onClick={copySecret}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-2 rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 whitespace-nowrap">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
              <button onClick={() => setNewKeySecret(null)}
                className="text-xs px-2.5 py-2 rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100">Done</button>
            </div>
          </div>
        )}

        {keys.length === 0 ? (
          <p className="text-sm text-[#94a3b8]">No API keys.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
                <tr><th className="text-left py-2">Name</th><th className="text-left">Type</th><th className="text-left">Owner</th><th className="text-left">Status</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-black/[0.04]">
                    <td className="py-2.5 text-[#0f172a] font-medium">{k.name} <span className="text-[#94a3b8] font-mono text-xs">{k.keyPreview}</span></td>
                    <td className="text-[#64748b]">{k.keyType}</td>
                    <td className="text-[#64748b]">{k.ownerName}{k.ownerEmail ? ` (${k.ownerEmail})` : ''}</td>
                    <td>{k.isActive ? <span className="text-xs text-green-600">Active</span> : <span className="text-xs text-[#94a3b8]">Revoked</span>}</td>
                    <td className="text-right">
                      <button onClick={() => askRevoke(k)} disabled={busy === k.id || !k.isActive}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40">
                        {busy === k.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
};

export default ClientUsersKeysSection;
